<template>
    <el-select
        v-scroll="scrollParams"
        :value="innerValue"
        :filter-method="innerFilterMethod"
        :multiple="multiple"
        @input="input"
        @change="change"
        @visible-change="visibleChange"
        v-bind="innerAttrs"
        v-on="listeners"
    >
        <el-option v-if="canSelectAll" value="__SELECT_ALL__" :class="{'selected': isSelectAll}" @click.native.capture.stop="hanldeSelectAll">全选</el-option>
        <el-option
            v-for="item in innerData"
            :label="renderLabel(item)"
            :value="item[valueKey]"
            :key="item[valueKey]"
            :disabled="item.disabled"
        ></el-option>
    </el-select>
</template>

<script>
import { get, isPlainObject, isNullOrUndefined, deepClone } from '@lsfe/tools';
import remoteMixin from '../../mixins/remote';
import { detectDeviceType } from '../../utils/tools';
/**
 * @author 方涛
 *
 * 基于 el-select 做的, 主要是做下拉框展示，可以传入数据或者函数，显示下拉框, 配置和 el-select 兼容
 */

export default {
    name: 'XxSelect',
    props: {
        /**
         * @model
         */
        value: {
            required: false
        },
        labelKey: {
            type: String,
            default: 'label'
        },
        valueKey: {
            type: String,
            default: 'value'
        },
        selectFirst: {
            type: Boolean,
            default: false
        },
        useScroll: {
            type: Boolean,
            default: true
        },
        filterMethod: {
            type: Function
        },
        multiple: {
            type: Boolean,
            default: false
        },
        disabledIds: {
            type: Array
        },
        showSelectAll: {
            type: Boolean,
            default: false,
        },
    },
    mixins: [remoteMixin()],
    data() {
        return {
            initialData: [],
            /**
             * list 的item必须是{label: '', value: '', hidden: false}
             */
            storeValue: this.value,
            data: [],
            scrollParams: {
                el: '.el-select-dropdown .el-select-dropdown__wrap',
                element: null,
                handleScroll: this.handleScroll,
                handleInserted: this.handleInserted,
                offset: 0,
                limit: 33,
                end: 33
            },
            innerValue: this.value,
            canScrollUp: true,
            scrollElement: '',
            querying: false, // 是否正在搜索
        };
    },
    computed: {
        innerAttrs() {
            let { renderLabel, list, ...attrs } = this.$attrs;
            attrs.clearable = attrs.clearable !== false;
            return {
                filterable: true,
                class: attrs.className || 'w180', // 添加默认宽度 可覆盖
                size: 'small',
                ...attrs
            };
        },
        listeners() {
            let { change, input, ...listeners } = this.$listeners;
            return listeners;
        },
        innerData() {
            let data = this.data || [];
            if (this.useScroll) {
                data = this.data.slice(this.scrollParams.offset, this.scrollParams.end).filter(v => !v.hidden);
            }
            return data.map(item => {
                if (this.disabledIds && this.disabledIds.includes(item[this.valueKey])) {
                    item.disabled = true;
                }
                return item;
            });
        },
        canSelectAll () {
            return this.showSelectAll && (this.canSelectAllData && this.canSelectAllData.length) && this.multiple && !this.querying;
        },
        isSelectAll() {
            return this.innerValue.length === this.canSelectAllData.length;
        },
        innerDataMap() {
            return (this.data || []).reduce((result, item) => {
                result[item[this.valueKey]] = item;
                return result;
            }, {});
        },
        canSelectAllData() {
            return this.initialData.map(item => item[this.valueKey]).filter(val => !(this.disabledIds || []).includes(val));
        }
    },
    watch: {
        '$attrs.list': function(v) {
            this.setData(v);
        },
        initialData(v) {
            this.data = deepClone(v);
            if (this.useScroll) {
                [this.scrollParams.offset, this.scrollParams.end] = [0, 33];
                if (!isNullOrUndefined(this.value)) {
                    this.setDefaultData(this.value);
                }
            }
        },
        value(val) {
            // 监听外部操作值的变化，对滚动数据进行处理
            if (val !== this.innerValue) {
                this.innerValue = val;
            }
            if (val !== this.storeValue) {
                // storeValue用来区分值的变化是由内部发生的还是外部传入的
                this.storeValue = val;
                [this.scrollParams.offset, this.scrollParams.end] = [0, 33];
                this.setDefaultData(val);
            }
        },
    },
    async created() {
        const { $attrs } = this;
        if ($attrs.list) {
            await this.setData($attrs.list);
        }
        if (!this.dicCode && this.selectFirst && this.data.length) {
            this.setFirstValue();
        }
        if (detectDeviceType() === 'Mobile') {
            this.mobileInteractionFix();
        }
    },
    methods: {
        mobileInteractionFix() {
            /* 解决移动端无法弹出软键盘的问题。去掉原el-select内部input的readonly属性， */
            Array.from(document.getElementsByClassName('el-select')).forEach(item => {
                item.children[0].children[0].removeAttribute('readonly');
                item.children[0].children[0].onblur = function () {
                    let _this = this;
                    setTimeout(() => {
                        _this.removeAttribute('readonly');
                    }, 200);
                };
            });
        },
        $_remoteDataLoaded(data) {
            let list = data;
            const { listPath = '', valueKey = 'value', labelKey = 'label' } = this.remoteConfig || {};
            if (listPath) {
                list = get(list, listPath);
            }
            this.initialData = list.map(item => {
                if (!isPlainObject(item)) {
                    return {
                        value: item,
                        label: item
                    };
                }
                return {
                    ...item,
                    value: get(item, valueKey),
                    label: get(item, labelKey)
                };
            });
            this.$nextTick(() => {
                if (this.selectFirst && this.data.length) {
                    this.setFirstValue();
                }
            });
        },
        setData(propList) {
            if (typeof propList === 'function') {
                return propList().then(list => {
                    this.initialData = list || [];
                });
            }
            if (propList && typeof propList.then === 'function') {
                return propList.then(list => {
                    this.initialData = list || [];
                });
            }
            return (this.initialData = propList || []);
        },
        innerFilterMethod(value) {
            this.querying = Boolean(value);
            [this.scrollParams.offset, this.scrollParams.end] = [0, 33];
            if (this.filterMethod && typeof this.filterMethod === 'function') {
                return this.filterMethod(value);
            }
            this.data = this.initialData.filter(item => {
                return `${this.renderLabel(item)}`.indexOf(value) !== -1;
            });
        },
        setDefaultData(val) {
            if (this.useScroll && this.data.length >= this.scrollParams.limit) {
                const key = this.valueKey;
                this.data.sort((prev, cur) => {
                    if (this.multiple && val && val.length) {
                        if (val.findIndex(item => item === prev[key]) !== -1) {
                            return -1;
                        }
                        if (val.findIndex(item => item === cur[key]) !== -1) {
                            return 1;
                        }
                    } else if (val) {
                        if (val === prev[key]) {
                            return -1;
                        }
                        if (val === cur[key]) {
                            return 1;
                        }
                    }
                    return 0;
                });
            }
        },
        setFirstValue() {
            const { valueKey } = this;
            const firstValue = isNullOrUndefined(this.data[0][valueKey]) ? this.value : this.data[0][valueKey];
            this.input(firstValue);
            this.change(firstValue);
        },
        input(val) {
            this.storeValue = val;
            this.$emit('input', val);
        },
        renderLabel(item) {
            const renderLabel = this.$attrs.renderLabel || this.$attrs['render-label'] || (item => get(item, this.labelKey));
            return renderLabel(item);
        },
        handleInserted(el) {
            this.scrollElement = el;
        },
        handleScroll(el, position) {
            this.scrollParams.element = el;
            if (this.useScroll) {
                let { offset, limit } = this.scrollParams;
                // let limmitDistance = el.scrollHeight / 6; // 设置距离边缘的距离
                let limmitDistance = 100; // 设置距离边缘的距离
                let scrollTo = el.scrollTop - position < 0 ? 'up' : 'down';
                let resetScrollTop = false; // 定义重置scrollTop的控制器
                // let offset = offset; // 偏移的下拉选项的数量，用来计算需要越变的scrollTop的距离
                if (scrollTo === 'up') {
                    // canScrollUp用来防止滚动条下移的过程中的scrollTop位置变更,如果不做控制会存在下移过程中重置scrollTop导致up事件触发
                    if (!this.canScrollUp) {
                        return (this.canScrollUp = true);
                    }
                    // 滚动条上移
                    if (el.scrollTop <= limmitDistance) {
                        // 如果是加在的第一项，不操作
                        if (offset <= 0) {
                            return;
                        }
                        // 控制offset最小为0
                        if (offset < limit) {
                            this.scrollParams.offset = 0;
                        } else {
                            this.scrollParams.offset -= limit;
                        }
                        // 计算偏移量
                        // const scrollLen = offset - this.scrollParams.offset;
                        // 超出渲染的下拉项数量限制，scrollEnd变化
                        if (this.scrollParams.end - this.scrollParams.offset > 3 * limit) {
                            this.scrollParams.end = this.scrollParams.offset + 3 * limit;
                            resetScrollTop = true;
                        }
                    }
                } else if (scrollTo === 'down') {
                    // 滚动条下移
                    if (el.scrollTop + el.clientHeight + limmitDistance >= el.scrollHeight) {
                        // 如果现在是加载最后一项，不操作
                        if (this.scrollParams.end >= this.data.length) {
                            return;
                        }
                        // 否则进行移动
                        if (this.scrollParams.end + limit > this.data.length) {
                            this.scrollParams.end = this.data.length;
                        } else {
                            this.scrollParams.end += limit;
                        }
                        if (this.scrollParams.end - this.scrollParams.offset > 3 * limit) {
                            // 超出渲染的下拉项数量限制
                            // 记录一下offset的距离 用来计算抵消越变的滚动距离
                            this.scrollParams.offset = this.scrollParams.end - 3 * limit + 1;
                            offset = this.scrollParams.offset - offset - 1;
                            resetScrollTop = true;
                        }
                        if (resetScrollTop) {
                            this.canScrollUp = false;
                            // 处理越变
                            el.scrollTop = el.scrollTop - offset * (el.scrollHeight / (3 * limit));
                        }
                    }
                }
            }
        },
        change(v) {
            const { valueKey } = this;
            let item;

            if (this.multiple) {
                item = v.map(val => this.innerDataMap[val]);
            } else {
                item = this.data.find(item => item[valueKey] == v);
            }

            /**
             * 切换select时触发 (value, item)
             *
             * @event change
             * @type {value}
             */
            this.$emit('change', v, item);
        },
        visibleChange(visible) {
            if (!visible) {
                return (this.data = deepClone(this.initialData));
            }
            if (visible && this.scrollParams.offset !== 0 && this.scrollParams.element) {
                // 设置scrollTop不等于0，防止在打开下拉框时无法向上滑动
                this.$nextTick(() => {
                    this.scrollParams.element.scrollTop = 10;
                });
            }
        },
        selectAllData() {
            this.input(this.canSelectAllData);
            this.change(this.canSelectAllData);
        },
        hanldeSelectAll() {
            if (this.isSelectAll) {
                this.input([]);
                this.change([]);
            } else {
                this.selectAllData();
            }
        }

    }
};
</script>
